import { IImage, ExperienceGPTAnswer, ArticleAuthor, UserDetailUtil } from 'shared/models';
import { ImageObject, Article, Person, InteractionCounter } from 'schema-dts';
import type { TFunction } from 'i18next';
import { dateToLocalISO } from 'shared/utils/format/time-formatter';
import { Region } from '@lemon8/web-app-shared/i18n';

export const ExperienceJsonLDUtil = {
  getTitle: (props: { experience: ExperienceGPTAnswer; t: TFunction }): string => {
    const { experience, t } = props;

    const articleTitle = experience.question;
    const publishYear = new Date(experience.submitTimeStamp * 1000).getFullYear().toString();
    return `${articleTitle} | ${publishYear} ${t('seo_searchCard_link_title')}`;
  },
  getDescription: (props: { experience: ExperienceGPTAnswer }): string => {
    const { experience } = props;
    const { answerContent, articleShortContent, isNewType } = experience;

    return isNewType ? articleShortContent : `${answerContent[0].title}: ${answerContent[0].content}`;
  },
  getKeywords: (props: { experience: ExperienceGPTAnswer }): string[] => {
    const { experience } = props;

    const result: string[] = [];

    const articleTitle = experience.question;
    if (articleTitle) {
      result.push(articleTitle);
    }
    result.push('Lemon8');
    return result;
  },
  getArticleJsonLD: (props: {
    canonicalUrl: string;
    experience: ExperienceGPTAnswer;
    t: TFunction;
    abInfo: {
      addCESOnExperience?: boolean;
    };
  }): Article => {
    const { experience, canonicalUrl, abInfo } = props;
    const { addCESOnExperience = false } = abInfo;
    const { articleInfoList, submitTimeStamp, aigcTitle, aigcDescription } = experience;

    const jsonLDTitle = aigcTitle || ExperienceJsonLDUtil.getTitle(props);
    const jsonLDDescription = aigcDescription || ExperienceJsonLDUtil.getDescription(props);
    const jsonLDKeywords = ExperienceJsonLDUtil.getKeywords(props);

    const articleTitle = experience.question;

    const imageList: ImageObject[] = [];
    const galleryImageList: IImage[] = [];
    const ocrText: string[] = [];
    const imgCaptions: string[] = [];
    const diggCount = experience.diggCount;

    articleInfoList.forEach((article) => {
      const { largeImage, ocrTextArr = [] } = article;
      galleryImageList.push(largeImage);
      ocrText.push(ocrTextArr[0]);
      imgCaptions.push(article.title);
    });

    const imageListWithExtraInfo = galleryImageList.map((image, index) => {
      return {
        ...image,
        imgOcrText: ocrText[index] || '',
        imgCaption: imgCaptions[index] || '',
      };
    });

    imageList.push(
      ...imageListWithExtraInfo.map((image: IImage & { imgCaption: string; imgOcrText: string }): ImageObject => {
        const { imgCaption } = image;
        return {
          '@type': 'ImageObject',
          url: image.url,
          contentUrl: image.url,
          representativeOfPage: 'True',
          keywords: [imgCaption, articleTitle, 'Lemon8'],
          caption: imgCaption,
          embeddedTextCaption: image.imgOcrText || '',
        };
      }),
    );

    const interactionStatistic: InteractionCounter[] = [
      {
        '@type': 'InteractionCounter',
        interactionType: {
          '@type': 'LikeAction',
        },
        userInteractionCount: diggCount,
      },
    ];

    if (addCESOnExperience) {
      interactionStatistic.push(
        {
          '@type': 'InteractionCounter',
          interactionType: {
            '@type': 'ViewAction',
          },
          userInteractionCount: experience.readCount,
        },
        {
          '@type': 'InteractionCounter',
          interactionType: {
            '@type': 'CommentAction',
          },
          userInteractionCount: experience.commentCount,
        },
      );
    }

    const articleBaseJsonLD: Article = {
      '@type': 'SocialMediaPosting',
      headline: jsonLDTitle,
      description: jsonLDDescription,
      keywords: jsonLDKeywords,
      url: canonicalUrl,

      datePublished: dateToLocalISO(submitTimeStamp, /** currently fix timezone to JP. @lidiang */ 9),

      image: imageList,

      mainEntityOfPage: {
        '@id': canonicalUrl,
        '@type': 'WebPage',
      },
      interactionStatistic,
    };

    return articleBaseJsonLD;
  },
  getPersonJsonLD: (props: { user: ArticleAuthor; userRegion: Region }): Person => {
    const { user, userRegion } = props;

    const person: Person = {
      '@type': 'Person',
      url: `https://www.lemon8-app.com${UserDetailUtil.getPath(user)}?region=${userRegion}`,
      name: user.nickName,
      alternateName: user.linkName,
      identifier: {
        '@type': 'PropertyValue',
        propertyID: 'Username',
        value: user.linkName,
      },
    };

    const interactionStatistic: InteractionCounter[] = [];

    if (user.followerCount) {
      interactionStatistic.push({
        '@type': 'InteractionCounter',
        interactionType: {
          '@type': 'FollowAction',
        },
        userInteractionCount: Number(user.followerCount),
      });
    }

    if (user.publishCount) {
      interactionStatistic.push({
        '@type': 'InteractionCounter',
        interactionType: {
          '@type': 'WriteAction',
        },
        userInteractionCount: Number(user.publishCount),
      });
    }

    person.interactionStatistic = interactionStatistic;

    return person;
  },
};
